import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import {
  ApplicationInsights,
  ITelemetryItem,
  ITelemetryPlugin,
} from '@microsoft/applicationinsights-web';

/**
 * Options for configuring the Application Insights integration.
 */
export interface AppInsightsOptions {
  /** Your Application Insights instrumentation key. */
  instrumentationKey: string;
  /**
   * Optional: Initial page name to track.  If not provided, the first page
   * view will need to be tracked manually with trackPageView.  Useful
   * if you want a custom name for the initial load, or if you're using
   * server-side rendering and the component doesn't mount immediately.
   */
  initialPageName?: string;
  /**
   * Optional: Enable automatic tracking of history changes (page views)
   * when using React Router (v4+).  Highly recommended.
   */
  enableAutoRouteTracking?: boolean;
  /**
   * Optional: Callback function to process telemetry items before sending.
   * This allows for filtering, modifying, or enriching telemetry data.
   * Return `false` to drop the telemetry item.  Return `true` or
   * a modified `ITelemetryItem` to send it.
   */
  beforeSendTelemetry?: (
    item: ITelemetryItem
  ) => boolean | ITelemetryItem | void;
  /**
   * Optional: An array of extra telemetry plugins to use, in addition to the ReactPlugin.
   */
  extraPlugins?: ITelemetryPlugin[];
}

// Singleton instances
let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;

/**
 * Checks if Application Insights has been initialized.
 */
export function isInitialized(): boolean {
  return appInsights !== null;
}

/**
 * Ensures Application Insights is initialized before operations.
 * @throws {Error} If Application Insights is not initialized.
 */
function ensureInitialized(): ApplicationInsights {
  if (!appInsights) {
    throw new Error(
      'Application Insights is not initialized. Call initializeAppInsights first.'
    );
  }
  return appInsights;
}

/**
 * Initializes and configures Application Insights as a singleton.
 *
 * @param options Configuration options for Application Insights.
 * @returns A tuple containing the ApplicationInsights instance and the ReactPlugin.
 * @throws {Error} If the instrumentation key is missing.
 */
export function initializeAppInsights(
  options: AppInsightsOptions
): [ApplicationInsights, ReactPlugin] {
  if (!options.instrumentationKey) {
    throw new Error('Application Insights instrumentation key is required.');
  }

  if (appInsights) {
    console.warn(
      'Application Insights is already initialized. Reinitializing...'
    );
  }

  reactPlugin = new ReactPlugin();
  appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: options.instrumentationKey,
      extensions: [reactPlugin, ...(options.extraPlugins || [])],
      extensionConfig: {
        [reactPlugin.identifier]: {
          //Keep reference to the reactPlugin, otherwise the plugin will be garbage collected
        },
      },
      enableAutoRouteTracking: options.enableAutoRouteTracking ?? false,
    },
  });

  appInsights.loadAppInsights();

  // Custom telemetry processor (optional).
  if (options.beforeSendTelemetry) {
    appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
      const result = options.beforeSendTelemetry!(item);
      if (result === false) {
        return false;
      } else if (result && typeof result !== 'boolean') {
        Object.assign(item, result);
      }
      return true;
    });
  }

  // Track the initial page view if initialPageName is provided.
  if (options.initialPageName) {
    appInsights.trackPageView({ name: options.initialPageName });
  }

  return [appInsights, reactPlugin];
}

/**
 * Tracks a custom event.
 *
 * @param eventName The name of the custom event.
 * @param properties Optional: Custom properties to associate with the event.
 */
export function trackEvent(
  eventName: string,
  properties?: { [key: string]: string }
) {
  try {
    const ai = ensureInitialized();
    ai.trackEvent({ name: eventName }, properties);
  } catch (error) {
    console.warn('Failed to track event:', error);
  }
}

/**
 * Tracks a page view.
 *
 * @param name Optional: The name of the page. Defaults to the current document title.
 * @param uri Optional: The URI of the page. Defaults to the current document location.
 * @param properties Optional: Custom properties to associate with the page view.
 * @param measurements Optional: Custom numeric measurements.
 */
export function trackPageView(
  name?: string,
  uri?: string,
  properties?: { [key: string]: string },
  measurements?: { [key: string]: number }
) {
  try {
    const ai = ensureInitialized();
    ai.trackPageView({ name, uri, properties, measurements });
  } catch (error) {
    console.warn('Failed to track page view:', error);
  }
}

/**
 * Tracks an exception.
 *
 * @param exception The Error object or a custom error object.
 * @param severityLevel Optional: The severity level of the exception.  Defaults to Error.
 * @param properties Optional: Custom properties.
 */
export function trackException(
  exception: Error | { message?: string; name?: string },
  severityLevel?: number,
  properties?: { [key: string]: string }
) {
  try {
    const ai = ensureInitialized();

    // Convert custom error object to proper Error if needed
    const errorObj =
      exception instanceof Error
        ? exception
        : new Error(exception.message || 'Unknown error');

    ai.trackException({ exception: errorObj, severityLevel }, properties);
  } catch (error) {
    console.warn('Failed to track exception:', error);
  }
}

/**
 * Sets the authenticated user context. Call this after a user successfully logs in.
 *
 * @param authenticatedUserId The user's ID. Do NOT include PII (personally identifiable information).
 * @param accountId Optional: The account ID associated with the user.
 * @param storeInCookie Optional: Whether to store the user context in a cookie. Defaults to false.
 */
export function setAuthenticatedUserContext(
  authenticatedUserId: string,
  accountId?: string,
  storeInCookie: boolean = false
) {
  try {
    const ai = ensureInitialized();
    ai.setAuthenticatedUserContext(
      authenticatedUserId,
      accountId,
      storeInCookie
    );
  } catch (error) {
    console.warn('Failed to set authenticated user context:', error);
  }
}

/**
 * Clears the authenticated user context. Call this when the user logs out.
 */
export function clearAuthenticatedUserContext() {
  try {
    const ai = ensureInitialized();
    ai.clearAuthenticatedUserContext();
  } catch (error) {
    console.warn('Failed to clear authenticated user context:', error);
  }
}

/**
 * Starts timing a custom operation. Call `stopTrackOperation` to stop timing and log the event.
 * @param operationName The name of the operation to track.
 */
export function startTrackOperation(operationName: string) {
  try {
    const ai = ensureInitialized();
    ai.startTrackEvent(operationName);
  } catch (error) {
    console.warn('Failed to start track operation:', error);
  }
}

/**
 * Stops timing a custom operation started with `startTrackOperation` and logs the event.
 * @param operationName The name of the operation to track.
 * @param properties Optional: Custom properties to associate with the operation.
 * @param measurements Optional: Custom numeric measurements to associate with the operation.
 */
export function stopTrackOperation(
  operationName: string,
  properties?: { [key: string]: string },
  measurements?: { [key: string]: number }
) {
  try {
    const ai = ensureInitialized();
    ai.stopTrackEvent(operationName, properties, measurements);
  } catch (error) {
    console.warn('Failed to stop track operation:', error);
  }
}

/**
 * Gets the ApplicationInsights instance (for advanced usage).
 * @returns The ApplicationInsights instance or null if not initialized.
 */
export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}

/**
 * Gets the ReactPlugin instance (for advanced usage).
 * @returns The ReactPlugin instance or null if not initialized.
 */
export function getReactPlugin(): ReactPlugin | null {
  return reactPlugin;
}
